import 'swiper/css'
import 'swiper/css/autoplay'
import 'swiper/css/effect-creative'
import 'swiper/css/pagination'

import { IconFont } from '@byecode/ui'
import type { ActorEvent, CarouselViewOptions, RecordLikeProtocol } from '@lighthouse/core'
import { DEFAULT_CAROUSEL_VIEW_OPTIONS } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import React, { forwardRef, memo, useMemo, useRef } from 'react'
import styled from 'styled-components'
import type { Swiper as SwiperInstance } from 'swiper'
import { Autoplay, EffectCreative, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useViewBlockContext } from '../../context'
import { Item } from './Item'
import { buildVerticalItemNodeData } from './utils'

const Root = styled.div`
    position: relative;
    min-width: 0;

    .swiper {
        width: 100%;
    }

    .swiper-pagination {
        position: absolute;
        bottom: 8px;
        left: 50%;
        width: auto;
        margin-top: 0;
        transform: translateX(-50%);
    }

    .swiper-pagination-bullet {
        background: rgb(217 217 217 / 40%);
        opacity: 1;
    }

    .swiper-pagination-bullet-active {
        background: #d9d9d9;
        opacity: 1;
    }
`

const NavigationButton = styled.button<{ position: 'left' | 'right' }>`
    position: absolute;
    z-index: 3;
    top: 50%;
    ${({ position }) => position}: 12px;
    display: flex;
    width: 32px;
    height: 32px;
    padding: 0;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    color: var(--color-white);
    background: rgb(16 24 40 / 45%);
    cursor: pointer;
    transform: translateY(-50%);

    &:hover {
        background: rgb(16 24 40 / 65%);
    }
`

interface Props {
    anchorId?: string
    node: FlowLayoutNode
    title: string
    readonly?: boolean
    options?: CarouselViewOptions
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

export const CarouselView = memo(
    forwardRef<HTMLDivElement, Props>(({ anchorId, node, title, readonly, options, onRecordClick }, ref) => {
        const swiperRef = useRef<SwiperInstance | null>(null)
        const { blockData, recordPoints } = useViewBlockContext()
        const { id } = blockData
        const resolvedOptions = { ...DEFAULT_CAROUSEL_VIEW_OPTIONS, ...options }
        const visibleRecordPoints = useMemo(() => (readonly ? recordPoints : recordPoints.slice(0, 1)), [readonly, recordPoints])
        const hasMultipleSlides = visibleRecordPoints.length > 1

        const slides = useMemo(
            () =>
                visibleRecordPoints.map((recordPoint, index) => ({
                    recordId: recordPoint.id,
                    index,
                    itemData: buildVerticalItemNodeData({
                        node,
                        blockId: id,
                        recordId: recordPoint.id,
                        index,
                        readonly,
                        widthSize: { size: 'fill' }
                    })
                })),
            [id, node, readonly, visibleRecordPoints]
        )

        if (!readonly) {
            const slide = slides[0]
            return (
                <Root ref={ref} id={anchorId}>
                    {slide && (
                        <Item
                            data-record-id={slide.recordId}
                            recordId={slide.recordId}
                            recordIndexPath="0"
                            data={slide.itemData}
                            title={title}
                            onRecordClick={onRecordClick}
                        />
                    )}
                </Root>
            )
        }

        const autoplay =
            hasMultipleSlides && resolvedOptions.autoplay
                ? {
                      delay: Math.max(0.1, resolvedOptions.interval) * 1000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true
                  }
                : false

        return (
            <Root ref={ref} id={anchorId}>
                <Swiper
                    autoHeight
                    allowTouchMove={hasMultipleSlides && resolvedOptions.swipe}
                    autoplay={autoplay}
                    creativeEffect={
                        resolvedOptions.effect === 'creative'
                            ? {
                                  prev: { translate: ['-120%', 0, -200], opacity: 0 },
                                  next: { translate: ['120%', 0, -200], opacity: 0 }
                              }
                            : undefined
                    }
                    effect={resolvedOptions.effect}
                    loop={hasMultipleSlides && resolvedOptions.loop}
                    modules={[Autoplay, EffectCreative, Pagination]}
                    pagination={hasMultipleSlides && resolvedOptions.pagination ? { clickable: true } : false}
                    preventClicks
                    preventClicksPropagation
                    threshold={5}
                    onSwiper={swiper => {
                        swiperRef.current = swiper
                    }}
                >
                    {slides.map(slide => (
                        <SwiperSlide key={slide.recordId}>
                            <Item
                                data-record-id={slide.recordId}
                                recordId={slide.recordId}
                                recordIndexPath={String(slide.index)}
                                readonly
                                data={slide.itemData}
                                title={title}
                                onRecordClick={onRecordClick}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>

                {hasMultipleSlides && resolvedOptions.navigation && (
                    <>
                        <NavigationButton type="button" position="left" aria-label="上一条" onClick={() => swiperRef.current?.slidePrev()}>
                            <IconFont type="ArrowLeftSmall" size={18} />
                        </NavigationButton>
                        <NavigationButton type="button" position="right" aria-label="下一条" onClick={() => swiperRef.current?.slideNext()}>
                            <IconFont type="ArrowRightSmall" size={18} />
                        </NavigationButton>
                    </>
                )}
            </Root>
        )
    })
)
